import "../Schedule/ScheduleView.scss";
import "./Presim.scss";
import { ADCSSvg, DataFlowSvg, PowerSvg } from "../../assets/icons/Icons";
import CustomToolTip from "../../../CommonComponents/CustomToolTip";
import { Fragment, useEffect, useState, useMemo } from "react";
import useSocket from "../../useSocket";
import PresimPanel from "./PresimPanel";
import { useDispatch, useSelector } from "react-redux";
import UnifiedControlPlaneService from "../../UnifiedControlPlane.service";
import MultigroupSelect from "../../../simulate/scenario/multigroup_select/MultigroupSelect";
import { setDefaultPredictionData, setDefaultPredictionForSatellite, setSelectedGroup, setSelectedSatellite, updatePredictionDataForSatellite } from "../../../../store/UnifiedControlPlaneSlice";

const PresimHeader = ({ scenarioId, mission_id }) => {

    const dispatch = useDispatch();

    const [showPanel, setShowPanel] = useState('');

    const scenario = useSelector(
        state => state.unifiedControlPlane.selectedScenario
    );
    const orderAssignments = useSelector(
        (state) => state.unifiedControlPlane.orderAssignments
    );

    const predictionDataResponse = useSelector(
        state => state.unifiedControlPlane.predictionDataResponse
    );

    const fidelity = useSelector((state) => state.unifiedControlPlane.fidelity)

    const predictionData = useSelector(state => state.unifiedControlPlane.presimData.predictionData);
    const satelliteGroups = useSelector(state => state.unifiedControlPlane.presimData.satelliteGroups);
    const selectedSatellite = useSelector(state => state.unifiedControlPlane.presimData.selectedSatellite);
    let selectedGroup = useSelector(state => state.unifiedControlPlane.presimData.selectedGroup);
    const allowPresimAnalyze = useSelector(state => state.unifiedControlPlane.allowPresimAnalyze);
    const previewScheduleId = useSelector((state) => state.unifiedControlPlane.previewScheduleId);
    const userData = useSelector(state => state.mission.userData);

    const filterSatelliteGroups = useMemo(() => {
        if (!satelliteGroups?.length || !orderAssignments?.length) return [];
        const assignmentSatellitesSet = new Set(
            orderAssignments
                .map(a => a?.data?.satellite?.id)
                .filter(Boolean)
        );
        const groupsMap = new Map();
        for (const group of satelliteGroups) {
            const satellites = group?.value?.satellites || [];
            for (const sat of satellites) {
                if (assignmentSatellitesSet.has(sat?.value?.satId)) {
                    groupsMap.set(group?.value?.groupId, group);
                    break; // 🚀 early exit (no need to check more sats)
                }
            }
        }
        return Array.from(groupsMap.values());
    }, [satelliteGroups, orderAssignments]);


    if (orderAssignments?.length) {
        const assignedSatIds = new Set(
            orderAssignments.map((a) => a?.data?.satellite?.id).filter(Boolean)
        );

        const filteredSatellites =
            selectedGroup?.value?.satellites?.filter((sat) =>
                assignedSatIds.has(sat.value.satId)
            ) || [];

        if (filteredSatellites.length > 0) {
            selectedGroup = {
                ...selectedGroup,
                value: {
                    ...selectedGroup.value,
                    satellites: filteredSatellites,
                },
            };
        }
    }

    const prediction_status = useMemo(() => predictionData && predictionData[selectedSatellite?.value.satId]?.state, [predictionData, selectedSatellite])
    const disabled_prediction_button = useMemo(() => predictionData && Object.keys(predictionData).find(satId => predictionData[satId]?.state === 'loading'), [prediction_status, predictionData])

    const handlePredictionDone = async () => {
        getPredictionData()
    };

    const getPredictionData = () => {
        const satelliteToSet = getSelectedSatellite()?.value?.satId;
        dispatch(setDefaultPredictionForSatellite({
            satelliteId: satelliteToSet,
            status: "loading",
        }))
        dispatch(UnifiedControlPlaneService.getPredictionData(mission_id, scenarioId, previewScheduleId, satelliteToSet, selectedGroup?.value?.groupId));
    }

    const handlePredictionFail = async () => {
        const satelliteId = selectedSatellite?.value?.satId
        dispatch(setDefaultPredictionForSatellite({
            satelliteId: satelliteId,
            status: "Error",
        }))
        console.log("Pre-sim is failed")
    }

    const set_selected_group = (group) => {
        dispatch(setSelectedGroup(group));
    }

    const set_selected_satellite = (satellite) => {
        dispatch(setSelectedSatellite(satellite));
    }

    useSocket({
        scenarioId,
        onStatusDone: handlePredictionDone,
        onStatusError: handlePredictionFail
    });

    const toggle_modal = (panel) => {
        setShowPanel(panel);
    };

    useEffect(() => {
        dispatch(setDefaultPredictionData(scenario))
    }, [scenario])

    const getSelectedSatellite = () => {
        if (!selectedGroup?.value?.satellites?.length) return;

        const found = selectedGroup.value.satellites.find(
            (sat) => sat.value.satName === selectedSatellite?.value?.satName
        );
        const satelliteToSet = found || selectedGroup.value.satellites[0];
        return satelliteToSet;
    }

    useEffect(() => {
        getSelectedSatellite();
        dispatch(setSelectedSatellite(getSelectedSatellite()));
    }, [selectedGroup, selectedSatellite, dispatch]);


    useEffect(() => {
        if (predictionDataResponse) {
            set_prediction_data(selectedGroup?.value?.groupId, selectedSatellite?.value?.satId)
        }
        /*** AT-11431 Fix ***/
        // else {
        //     post_prediction_data()
        // }
    }, [predictionDataResponse])

    useEffect(() => {
        if (!predictionDataResponse && prediction_status === 'loading') {
            post_prediction_data()
        }
    }, [predictionDataResponse, prediction_status])

    const set_prediction_data = async (groupId, satelliteId) => {
        try {
            if (predictionDataResponse?.[0]) {
                let agilityUrl = [];
                let powerUrl = [];
                let dataFlowUrl = [];
                const grafanaURLS = predictionDataResponse?.[0];
                const flagDataCompress = userData?.["feature-allowed"]?.["dataCompress"] === "Yes" ? true : false;
                if (grafanaURLS?.["satelliteGroupId"] === groupId) {
                    if (grafanaURLS?.["satelliteId"] === satelliteId) {
                        agilityUrl = grafanaURLS?.["schPredictionData"]?.["adcs"];
                        powerUrl = grafanaURLS?.["schPredictionData"]?.["power"];
                        dataFlowUrl = grafanaURLS?.["schPredictionData"]?.["dataflow"].filter((panel) =>
                            flagDataCompress
                                ? panel?.["panel_name"] !== "Summary"
                                : panel?.["panel_name"] !== "Data Summary"
                        );
                        dispatch(
                            updatePredictionDataForSatellite({
                                satelliteId,
                                agilityUrl,
                                powerUrl,
                                dataFlowUrl,
                            })
                        );
                    }
                }
            }
        } catch (error) {
            dispatch(
                setDefaultPredictionForSatellite({
                    satelliteId,
                    status: "pending",
                })
            );
        }
    };

    const post_prediction_data = () => {
        const satelliteId = selectedSatellite?.value?.satId
        const groupId = selectedGroup?.value?.groupId
        // This may need in future
        // const prediction_running = predictionData && Object.keys(predictionData).find(satId => predictionData[satId]?.state === 'loading')
        // if (prediction_running) return
        dispatch(setDefaultPredictionForSatellite({
            satelliteId,
            status: 'loading'
        }));
        try {
            dispatch(UnifiedControlPlaneService.postPredictionData(
                mission_id,
                scenarioId,
                groupId,
                satelliteId,
                previewScheduleId,
                fidelity
            ))
        } catch (error) {
            dispatch(setDefaultPredictionForSatellite({
                satelliteId,
                status: 'pending'
            }));
        }
    };

    return (
        <>
            {scenario && Object.keys(scenario).length > 0 &&
                <div className="presimBar">
                    <MultigroupSelect
                        groups={filterSatelliteGroups}
                        selected_group={selectedGroup}
                        set_selected_group={set_selected_group}
                        set_selected_satellite={set_selected_satellite}
                        satellites={selectedGroup?.value?.satellites}
                        selected_satellite={selectedSatellite}
                        prediction_data={predictionData}
                        disabled={!allowPresimAnalyze}
                    />

                    <div className="presim-config-options prediction__burrons__container">
                        {
                            prediction_status === 'success' &&
                            <Fragment>
                                <CustomToolTip title='Power' placement='bottom'>
                                    <button className={`right__section__button left__btn ${showPanel === 'power' ? 'active' : ''}`}
                                        onClick={() => { toggle_modal('power') }}>
                                        <PowerSvg />
                                    </button>
                                </CustomToolTip>
                                <CustomToolTip title='ADCS' placement='bottom'>
                                    <button className={`right__section__button center__btn ${showPanel === 'adcs' ? 'active' : ''}`}
                                        onClick={() => { toggle_modal('adcs') }}>
                                        <ADCSSvg />
                                    </button>
                                </CustomToolTip>
                                {<CustomToolTip title='Data Flow' placement='bottom'>
                                    <button className={`right__section__button right__btn ${showPanel === 'data_flow' ? 'active' : ''}`}
                                        onClick={() => { toggle_modal('data_flow') }}>
                                        <DataFlowSvg />
                                    </button>
                                </CustomToolTip>}
                            </Fragment>
                        }
                        {
                            prediction_status !== 'success' &&
                            <button
                                disabled={(!allowPresimAnalyze || disabled_prediction_button) ? true : false}
                                className={`prediction__button ${prediction_status}`}
                                onClick={getPredictionData}
                            >
                                <span className='prediction__button__text'>{prediction_status === 'loading' ? 'Analyzing' : 'Predict Schedule'}</span>
                                {prediction_status === 'loading' && <div className='prediction__loader' />}
                            </button>
                        }
                    </div>

                    {showPanel === 'power'
                        &&
                        <PresimPanel
                            header={'Power'}
                            icon={<PowerSvg />}
                            panelType={'powerUrl'}
                            closePanel={() => toggle_modal('')}
                        />}
                    {showPanel === 'adcs'
                        &&
                        <PresimPanel
                            header={'ADCS'}
                            icon={<ADCSSvg />}
                            panelType={'agilityUrl'}
                            closePanel={() => toggle_modal('')}
                        />}
                    {showPanel === 'data_flow'
                        &&
                        <PresimPanel
                            header={'Data Flow'}
                            icon={<DataFlowSvg />}
                            panelType={'dataFlowUrl'}
                            closePanel={() => toggle_modal('')}
                        />}

                </div>
            }
        </>
    );
}

export default PresimHeader;